import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eventsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Calendar, Clock, MoreVertical, Trash2, Edit, LogOut, Sun, Moon, Search, Users, UserCog, Copy, CheckCircle, XCircle } from 'lucide-react';
import { DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout, updateProfile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: '', description: '', scheduled_date: '', streaming_url: '' });
  const [creating, setCreating] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const handleEditProfile = () => {
    setProfileName(user?.name || '');
    setShowProfileDialog(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profileName.trim()) return;
    setSavingProfile(true);
    try {
      await updateProfile(profileName.trim());
      setShowProfileDialog(false);
      toast.success('Nombre actualizado');
    } catch {
      toast.error('Error al actualizar el nombre');
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await eventsApi.getAll();
      setEvents(res.data);
    } catch (error) {
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await eventsApi.create(newEvent);
      setEvents([res.data, ...events]);
      setShowCreateDialog(false);
      setNewEvent({ name: '', description: '', scheduled_date: '', streaming_url: '' });
      toast.success('Evento creado');
      navigate(`/event/${res.data.id}`);
    } catch (error) {
      toast.error('Error al crear evento');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('¿Estás seguro de eliminar este evento?')) return;
    try {
      await eventsApi.delete(eventId);
      setEvents(events.filter(e => e.id !== eventId));
      toast.success('Evento eliminado');
    } catch (error) {
      toast.error('Error al eliminar evento');
    }
  };

  const handleDuplicateEvent = async (eventId) => {
    try {
      const res = await eventsApi.duplicate(eventId);
      setEvents(prev => [res.data, ...prev]);
      toast.success('Evento duplicado');
    } catch {
      toast.error('Error al duplicar evento');
    }
  };

  const handleFinalizeEvent = async (eventId) => {
    if (!window.confirm('¿Marcar este evento como finalizado?')) return;
    try {
      const res = await eventsApi.update(eventId, { status: 'completed' });
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, status: res.data.status } : e));
      toast.success('Evento marcado como finalizado');
    } catch {
      toast.error('Error al finalizar evento');
    }
  };

  const handleCancelEvent = async (eventId) => {
    if (!window.confirm('¿Marcar este evento como cancelado?')) return;
    try {
      const res = await eventsApi.update(eventId, { status: 'cancelled' });
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, status: res.data.status } : e));
      toast.success('Evento marcado como cancelado');
    } catch {
      toast.error('Error al cancelar evento');
    }
  };

  const ownedEvents = events.filter(e => e.my_role !== 'client');
  const sharedEvents = events.filter(e => e.my_role === 'client');
  // User has no own events but is invited to some → pure client mode
  const isClientOnly = !loading && ownedEvents.length === 0 && sharedEvents.length > 0;

  const filteredOwned = ownedEvents.filter(event =>
    event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredShared = sharedEvents.filter(event =>
    event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: 'Borrador', class: 'bg-zinc-500/20 text-zinc-400' },
      live: { label: 'En vivo', class: 'bg-red-500/20 text-red-400 animate-pulse-live' },
      completed: { label: 'Completado', class: 'bg-green-500/20 text-green-400' },
      cancelled: { label: 'Cancelado', class: 'bg-zinc-700/40 text-zinc-500 line-through' },
    };
    return badges[status] || badges.draft;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-enubes.webp" alt="eNubes" className="h-7 dark:invert-0 invert" />
            <span className="text-muted-foreground mx-1">|</span>
            <span className="font-heading text-lg font-semibold tracking-tight">EventRun</span>
          </div>

          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium">{user?.name?.[0]?.toUpperCase()}</span>
                  </div>
                  <span className="hidden sm:inline text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleEditProfile}>
                  <UserCog className="w-4 h-4 mr-2" />
                  Editar nombre
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              {isClientOnly ? 'Eventos compartidos' : 'Mis Eventos'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isClientOnly ? 'Eventos a los que has sido invitado' : 'Gestiona tus rundowns y eventos en vivo'}
            </p>
          </div>

          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            {!isClientOnly && (
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-event-btn">
                <Plus className="w-4 h-4" />
                Nuevo Evento
              </Button>
            </DialogTrigger>
            )}
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Crear nuevo evento</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="event-name">Nombre del evento</Label>
                  <Input
                    id="event-name"
                    placeholder="Ej: Conferencia Tech 2025"
                    value={newEvent.name}
                    onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                    required
                    data-testid="event-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event-description">Descripción</Label>
                  <Textarea
                    id="event-description"
                    placeholder="Descripción del evento..."
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    data-testid="event-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event-date">Fecha programada</Label>
                  <Input
                    id="event-date"
                    type="datetime-local"
                    value={newEvent.scheduled_date}
                    onChange={(e) => setNewEvent({ ...newEvent, scheduled_date: e.target.value })}
                    data-testid="event-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event-streaming-url">URL de streaming (opcional)</Label>
                  <Input
                    id="event-streaming-url"
                    type="url"
                    placeholder="https://youtube.com/watch?v=... o https://twitch.tv/..."
                    value={newEvent.streaming_url}
                    onChange={(e) => setNewEvent({ ...newEvent, streaming_url: e.target.value })}
                    data-testid="event-streaming-url-input"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={creating} data-testid="submit-event-btn">
                    {creating ? 'Creando...' : 'Crear evento'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar eventos..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-events-input"
          />
        </div>

        {/* Events grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-1/2 mt-2" />
                </CardHeader>
                <CardContent>
                  <div className="h-4 bg-muted rounded w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
            {/* Mis eventos (organizador) */}
            {!isClientOnly && filteredOwned.length === 0 && !searchQuery ? (
              <div className="empty-state">
                <Calendar className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">Sin eventos aún</h3>
                <p className="text-muted-foreground mb-6">Crea tu primer evento para comenzar</p>
                <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Crear evento
                </Button>
              </div>
            ) : isClientOnly || (filteredOwned.length === 0 && searchQuery) ? null : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                {filteredOwned.map(event => {
                  const badge = getStatusBadge(event.status);
                  return (
                    <Card
                      key={event.id}
                      className="group cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => navigate(`/event/${event.id}`)}
                      data-testid={`event-card-${event.id}`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <CardTitle className="font-heading text-lg truncate">{event.name}</CardTitle>
                            <CardDescription className="line-clamp-2 mt-1">
                              {event.description || 'Sin descripción'}
                            </CardDescription>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/event/${event.id}`); }}>
                                <Edit className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicateEvent(event.id); }}>
                                <Copy className="w-4 h-4 mr-2" />
                                Duplicar
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {event.status !== 'completed' && event.status !== 'cancelled' && (
                                <DropdownMenuItem
                                  className="text-green-500"
                                  onClick={(e) => { e.stopPropagation(); handleFinalizeEvent(event.id); }}
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Finalizar evento
                                </DropdownMenuItem>
                              )}
                              {event.status !== 'cancelled' && (
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={(e) => { e.stopPropagation(); handleCancelEvent(event.id); }}
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancelar evento
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {event.scheduled_date && (
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                <span>{format(new Date(event.scheduled_date), 'dd MMM', { locale: es })}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              <span>{format(new Date(event.created_at), 'HH:mm')}</span>
                            </div>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wider ${badge.class}`}>
                            {badge.label}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Eventos compartidos conmigo (cliente) */}
            {filteredShared.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <h2 className="font-heading text-xl font-semibold">Eventos compartidos conmigo</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredShared.map(event => {
                    const badge = getStatusBadge(event.status);
                    return (
                      <Card
                        key={event.id}
                        className="group cursor-pointer hover:border-blue-500/50 transition-colors border-dashed"
                        onClick={() => navigate(`/event/${event.id}/client`)}
                        data-testid={`shared-event-card-${event.id}`}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded">Invitado</span>
                              </div>
                              <CardTitle className="font-heading text-lg truncate">{event.name}</CardTitle>
                              <CardDescription className="line-clamp-2 mt-1">
                                {event.description || 'Sin descripción'}
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              {event.scheduled_date && (
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5" />
                                  <span>{format(new Date(event.scheduled_date), 'dd MMM', { locale: es })}</span>
                                </div>
                              )}
                            </div>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wider ${badge.class}`}>
                              {badge.label}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Profile dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Editar nombre</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveProfile} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="profile-name">Nombre</Label>
              <Input
                id="profile-name"
                value={profileName}
                onChange={e => setProfileName(e.target.value)}
                placeholder="Tu nombre"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowProfileDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={savingProfile || !profileName.trim()}>
                {savingProfile ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
